<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import MainTitleComponent from '@/components/ui/MainTitleComponent.vue';
import ReferralRewardService from '@/services/referralReward/referralRewardService.ts';
import { ReferralRewardStatusEnum } from '@/enums/referralReward_enum.ts';
import {
  formatEuros,
  formatFullDate,
  formatItemValue,
  formatReferralRewardStatus
} from '@/utils/formatters.ts';

import type { DataTableHeader } from 'vuetify/framework';
import type {
  ReferralReward,
  ReferralRewardSummary,
  ReferralUser
} from '@/types/services/referralReward/referralReward.type.ts';

const { t } = useI18n()
const router = useRouter()

const rewards = ref<ReferralReward[]>([])
const summary = ref<ReferralRewardSummary | null>(null)
const loading = ref<boolean>(false)

const statusFilter = ref<ReferralRewardStatusEnum | null>(null)
const referrerId = ref<string>('')
const refereeId = ref<string>('')
const limit = ref<number>(10)
const page = ref<number>(1)
const total = ref<number>(0)
const maxPages = ref<number>(1)

const statusItems = computed(() => [
  { title: t('views.referralRewardList.allStatuses'), value: null },
  ...Object.values(ReferralRewardStatusEnum).map(status => ({
    title: t(`views.referralRewardList.status.${status}`),
    value: status
  }))
])

const headers = computed<DataTableHeader[]>(() => [
  { title: t('views.referralRewardList.header.id'), key: 'id', sortable: false, align: 'start' },
  { title: t('views.referralRewardList.header.status'), key: 'status', sortable: false, align: 'start' },
  { title: t('views.referralRewardList.header.amount'), key: 'amount', sortable: false, align: 'start' },
  { title: t('views.referralRewardList.header.referrer'), key: 'referrer', sortable: false, align: 'start' },
  { title: t('views.referralRewardList.header.referee'), key: 'referee', sortable: false, align: 'start' },
  { title: t('views.referralRewardList.header.attempts'), key: 'attempts', sortable: false, align: 'start' },
  { title: t('views.referralRewardList.header.lastError'), key: 'lastError', sortable: false, align: 'start' },
  { title: t('views.referralRewardList.header.notified'), key: 'notifiedAt', sortable: false, align: 'start' },
  { title: t('views.referralRewardList.header.createdAt'), key: 'createdAt', sortable: false, align: 'start' },
])

// Rewards waiting for a human: surfaced first in the summary.
const actionRequiredStatuses = [ReferralRewardStatusEnum.REVIEW, ReferralRewardStatusEnum.FAILED]

function summaryCount(status: ReferralRewardStatusEnum): number {
  return summary.value?.statuses.find(s => s.status === status)?.count ?? 0
}

function parseId(value: string): number | null {
  const parsed = Number(value)
  return value.trim() !== '' && Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function userLabel(user: ReferralUser): string {
  return `${user.firstName} ${user.lastName}`.trim() || user.email
}

async function fetchRewards(): Promise<void> {
  try {
    loading.value = true
    const response = await ReferralRewardService.getPaginated(page.value, limit.value, {
      status: statusFilter.value,
      referrerId: parseId(referrerId.value),
      refereeId: parseId(refereeId.value)
    })
    rewards.value = response.data
    total.value = response.total
    maxPages.value = response.maxPages
  } finally {
    loading.value = false
  }
}

async function fetchSummary(): Promise<void> {
  summary.value = await ReferralRewardService.getSummary()
}

onMounted(() => {
  fetchRewards()
  fetchSummary()
})

// Any filter change restarts from page 1.
watch([statusFilter, referrerId, refereeId], () => {
  if (page.value !== 1) {
    page.value = 1
  } else {
    fetchRewards()
  }
})
watch([limit, page], fetchRewards)

function filterByStatus(status: ReferralRewardStatusEnum): void {
  statusFilter.value = statusFilter.value === status ? null : status
}

async function onRowClick(_: MouseEvent, row: { item: ReferralReward }): Promise<void> {
  await router.push(`/referral-rewards/${row.item.id}`)
}
</script>

<template>
  <MainTitleComponent icon="PhGift">
    {{ t('views.referralRewardList.title') }}
  </MainTitleComponent>

  <!-- Summary: action-required statuses first, then the other counters -->
  <div v-if="summary" class="summary-grid mb-4">
    <v-card
      v-for="status in [...actionRequiredStatuses, ...Object.values(ReferralRewardStatusEnum).filter(s => !actionRequiredStatuses.includes(s))]"
      :key="status"
      :color="statusFilter === status ? 'primary' : undefined"
      :variant="statusFilter === status ? 'flat' : 'outlined'"
      class="summary-card pa-3"
      @click="filterByStatus(status)"
    >
      <div class="text-caption summary-label">{{ t(`views.referralRewardList.status.${status}`) }}</div>
      <div class="d-flex align-center justify-space-between">
        <span class="text-h6">{{ summaryCount(status) }}</span>
        <v-chip
          v-if="actionRequiredStatuses.includes(status) && summaryCount(status) > 0"
          color="error"
          size="x-small"
        >
          {{ t('views.referralRewardList.actionRequired') }}
        </v-chip>
      </div>
    </v-card>
  </div>

  <v-row align="center">
    <v-col cols="12" sm="4">
      <v-select
        v-model="statusFilter"
        :items="statusItems"
        :label="t('views.referralRewardList.filters.status')"
        density="compact"
        hide-details
      />
    </v-col>
    <v-col cols="6" sm="4">
      <v-text-field
        v-model="referrerId"
        :label="t('views.referralRewardList.filters.referrerId')"
        density="compact"
        hide-details
        inputmode="numeric"
      />
    </v-col>
    <v-col cols="6" sm="4">
      <v-text-field
        v-model="refereeId"
        :label="t('views.referralRewardList.filters.refereeId')"
        density="compact"
        hide-details
        inputmode="numeric"
      />
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <v-data-table-server
        :headers="headers"
        :items="rewards"
        :items-length="total"
        :items-per-page="limit"
        :loading="loading"
        :no-data-text="t('common.noResult')"
        :page="page"
        hover
        @update:page="page = $event"
        @update:items-per-page="limit = $event"
        @click:row="onRowClick"
      >
        <template #loading>
          <v-skeleton-loader type="table-row@4" />
        </template>
        <template #[`item.status`]="{ item }">
          <v-chip :color="formatReferralRewardStatus(item.status)" size="small">
            {{ t(`views.referralRewardList.status.${item.status}`) }}
          </v-chip>
        </template>
        <template #[`item.amount`]="{ item }">
          {{ formatEuros(item.amount) }}
        </template>
        <template #[`item.referrer`]="{ item }">
          {{ userLabel(item.referrer) }}
        </template>
        <template #[`item.referee`]="{ item }">
          {{ userLabel(item.referee) }}
        </template>
        <template #[`item.lastError`]="{ item }">
          {{ formatItemValue(item.lastError) }}
        </template>
        <template #[`item.notifiedAt`]="{ item }">
          <v-icon
            :color="item.notifiedAt ? 'success' : undefined"
            :icon="item.notifiedAt ? 'PhEnvelopeSimple' : 'PhMinus'"
            size="small"
          />
        </template>
        <template #[`item.createdAt`]="{ item }">
          {{ formatFullDate(item.createdAt) }}
        </template>
        <template #bottom>
          <v-pagination
            v-model="page"
            :length="maxPages"
            :tile="true"
            density="compact"
            total-visible="6"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>
</template>

<style scoped>
/* Every summary card gets the exact same size, whatever its label or content. */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 190px);
  gap: 12px;
}

.summary-card {
  height: 84px;
  cursor: pointer;
}

.summary-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
